import { FC } from "preact/compat";
import { useSignal } from "@preact/signals";
import { useApp } from "~/core/store";
import { Button, Dialog } from "~/components";
import ConfirmationDialog from "./ConfirmationDialog";
import ContentEditor from "./ContentEditor";
import Guide from "./Guide";
import Sidebar from "./Sidebar";
import { FALLBACK_IDX } from "./utils";
import BookIcon from "@material-design-icons/svg/outlined/book.svg";

const DEFAULT_EDIT_LOCALE = "EN";

const DiscoveryEditor: FC = () => {
    const {
        help: {
            tags,
            isDiscovery,
            admin: {
                isAdmin,
                isGuide,
                isEditing,
                defaultTab,
                editorTag,
                unsaved,
                closeEditor,
            },
        },
    } = useApp();
    const confirmation = useSignal(false);
    const locale = useSignal(DEFAULT_EDIT_LOCALE);
    const activeTab = useSignal(defaultTab.peek() ?? 0);

    if (!isAdmin.value || !isDiscovery.value) {
        return null;
    }

    const currentTag = tags.value.find((tag) => tag.tag === editorTag.value);
    const fallbackTag = tags.value.find(
        (tag) => tag.tag === `${editorTag.value}?`,
    );
    const sidebarTag =
        activeTab.value === FALLBACK_IDX ? fallbackTag : currentTag;

    return (
        <>
            <ConfirmationDialog locale={locale} />
            <Dialog
                show={isEditing}
                onClose={() => {
                    if (unsaved.peek().length > 0) {
                        confirmation.value = true;
                    } else {
                        closeEditor();
                    }
                }}
            >
                <div className="flex w-[1600px] max-w-[calc(100vw-120px)] rounded bg-white shadow-lg">
                    <div className="flex w-[300px] min-w-[300px] flex-col justify-between border-r border-gray-300">
                        <div className="relative h-full w-full overflow-hidden">
                            <Sidebar
                                tag={sidebarTag}
                                className="overflow-y-overlay h-[calc(90vh-67px)] max-h-[calc(90vh-67px)] w-full"
                            />
                        </div>
                        <div className="flex justify-center border-t border-gray-300 bg-gray-100 p-4">
                            <Button
                                variant="secondary"
                                onClick={() => (isGuide.value = !isGuide.value)}
                                className="gap-x-2"
                            >
                                <BookIcon width="18" height="18" />
                                <span>
                                    {isGuide.value ? "Close" : "Open"} guide
                                </span>
                            </Button>
                        </div>
                    </div>
                    <div className="min-h-[90vh] w-full">
                        {isGuide.value ? (
                            <Guide />
                        ) : currentTag && fallbackTag ? (
                            <ContentEditor
                                key={currentTag.tag}
                                tag={currentTag}
                                fallbackTag={fallbackTag}
                                locale={locale}
                                activeTab={activeTab}
                            />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center text-lg text-gray-500">
                                <span>
                                    {editorTag.value
                                        ? `Tag "${editorTag.value}" not found. Contact a developer`
                                        : "Select a tag to edit in the left sidebar"}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default DiscoveryEditor;
